import { useCallback, useState } from 'react'

type ActionState<T> = {
  action: Action | null
  value: T | null
}

enum Action {
  CREATE,
  READ,
  UPDATE,
  DELETE,
}

const NO_ACTION_STATE = { action: null, value: null }

/**
 * This hook is designed to help with scenarios where **_multiple mutually exclusive_** components may be rendered
 * based on an action taken by the user. Given their mutually exclusive nature, only a single disclosure
 * is warranted, as the different views cannot (by definition) be shown at the same time.
 * As such, this hook aims to provide an easy way to solve for this case, where the operations
 * that may be performed are: Create, View (Read), Edit (Update), and/or Delete.
 *
 * Example case with Edit or Delete operations:
 * @example
 * ```tsx
 * const {
 *   editValue,
 *   deleteValue,
 *   isDeleting,
 *   isEditing,'
 *   onEdit,
 *   onDelete,
 *   onClose,
 * } = useCrudDisclosure<MyObjectType>();
 * return (
 *   <>
 *     {editValue?.id && (
 *       <Modal
 *         isOpen={isEditing}
 *         onClose={onClose}>
 *         // modal content...
 *       </Modal>
 *     )}
 *
 *     {deleteValue?.id && (
 *       <DeleteModal
 *         someId={deleteValue?.id}
 *         isOpen={isDeleting}
 *         onClose={onClose}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
function useCrudDisclosure<T>() {
  const [{ action, value }, setActionState] = useState<ActionState<T>>(NO_ACTION_STATE)
  const onCreate = useCallback(() => setActionState({ action: Action.CREATE, value: null }), [])
  const onView = useCallback(
    (valueToView: T) => setActionState({ action: Action.READ, value: valueToView }),
    []
  )
  const onEdit = useCallback(
    (valueToEdit: T) => setActionState({ action: Action.UPDATE, value: valueToEdit }),
    []
  )
  const onDelete = useCallback(
    (valueToDelete: T) => setActionState({ action: Action.DELETE, value: valueToDelete }),
    []
  )
  const onClose = useCallback(() => setActionState(NO_ACTION_STATE), [])
  const isCreating = action === Action.CREATE
  const isViewing = action === Action.READ
  const isEditing = action === Action.UPDATE
  const isDeleting = action === Action.DELETE
  const isOpen = isCreating || isEditing || isDeleting
  return {
    isOpen,
    onClose,
    isCreating,
    isViewing,
    isEditing,
    isDeleting,
    onCreate,
    onView,
    onEdit,
    onDelete,
    viewValue: isViewing ? value : null,
    editValue: isEditing ? value : null,
    deleteValue: isDeleting ? value : null,
  }
}

export { useCrudDisclosure }
